import React, { CSSProperties, MutableRefObject, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import classnames from "classnames/dedupe";
import { Delta, Sources } from "quill";
import ReactQuill from "react-quill";
import { Label } from "reactstrap";

import { AudioRecorder } from "components/chatbot/components/audio-recorder";
import { COLORS_ARRAY } from "../../utils/colors-array";

import "./rich-text-input.scss";

// https://quilljs.com/docs/modules/toolbar/#container
const TOOLBAR_TEMPLATE = [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    [{ size: ["small", false, "large", "huge"] }],
    [{ color: COLORS_ARRAY }, { background: COLORS_ARRAY }],
    [{ font: [] }],
    [{ align: [] }],
    ["bold", "italic", "underline", "strike"], // toggled buttons
    [{ header: 1 }, { header: 2 }], // custom button values
    [{ list: "ordered" }, { list: "bullet" }],
    [{ script: "sub" }, { script: "super" }], // superscript/subscript
    [{ indent: "-1" }, { indent: "+1" }], // outdent/indent
    ["clean"],
];

interface IRichTextInput {
    id?: string;
    className?: string;
    inputClassName?: string;
    style?: CSSProperties;
    value?: string;
    label?: string;
    readOnly?: boolean;
    defaultValue?: string;
    placeholder?: string;
    toolbarTemplate?: any[];
    autofocus?: boolean;
    audioRecorder?: AudioRecorder | boolean;
    rtRef?: MutableRefObject<ReactQuill | null>;
    innerRef?: MutableRefObject<HTMLDivElement | null>;
    onChange?: ((content: string, delta: Delta, source: Sources, editor: any) => void) | undefined;
    onChangeSelection?: (range: IQuillRange, source: Sources, editor: any) => void;
    onFocus?: (range: IQuillRange) => void;
    onBlur?: (range: IQuillRange) => void;
    onKeyPress?: React.EventHandler<any>;
    onKeyDown?: React.EventHandler<any>;
    onKeyUp?: React.EventHandler<any>;
    onTranscribe?: (value: React.SetStateAction<string>) => void;
}

interface AudioRecorder {
    url?: string;
    onStop?: () => void;
}

export interface IQuillRange {
    index: number;
    length: number;
}

export const RichTextInput = (props: IRichTextInput) => {
    //! if using uncontrolled set defaultValue instead value

    const {
        id = "gt-rich-text-input",
        className,
        inputClassName,
        style,
        value,
        readOnly = false,
        defaultValue,
        placeholder = !readOnly ? "Start editing..." : "",
        toolbarTemplate = TOOLBAR_TEMPLATE,
        autofocus = false,
        audioRecorder,
        label,
        innerRef,
        onChange,
        onFocus,
        onBlur,
        onKeyPress,
        onKeyDown,
        onKeyUp,
        onChangeSelection,
        onTranscribe,
    } = props;
    const [_value, setValue] = useState<string>(defaultValue || "");

    let { rtRef: rtRefExternal } = props;

    const selectionRef = useRef<number | undefined>(undefined);

    const rtRef = useRef<ReactQuill | null>(null);

    const handleChange = (e) => {
        setValue(e);
    };

    const handleMouseDown = () => {
        const editor = rtRef.current?.getEditor();
        const selection = editor?.getSelection();
        if (selection?.index) selectionRef.current = selection?.index;
    };

    const handleTranscribe = (value) => {
        const editor = rtRef.current?.getEditor();
        const selection = editor?.getSelection();

        const getIndex = () => {
            if (typeof selection?.index == "number") return selection?.index;
            else return selectionRef.current || 0;
        };

        const index = getIndex();

        editor?.insertText(index, value);
    };

    const { t } = useTranslation();

    return (
        <div className={classnames("gt-rich-text-input-container", className)} ref={innerRef}>
            {label && <Label>{t(label)}:</Label>}
            <ReactQuill
                id={id}
                className={classnames("gt-rich-text-input", inputClassName)}
                style={style}
                value={value || _value}
                ref={(ref) => {
                    if (rtRefExternal) {
                        rtRefExternal.current = ref;
                    }
                    if (autofocus) {
                        ref?.focus();
                    }
                    rtRef.current = ref;
                }}
                readOnly={readOnly}
                placeholder={t(placeholder)}
                onChange={onChange || handleChange}
                onChangeSelection={onChangeSelection}
                onFocus={onFocus}
                onBlur={onBlur}
                onKeyPress={onKeyPress}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
                modules={{
                    toolbar: !readOnly && toolbarTemplate,
                }}
                theme="snow"
            />
            {!!audioRecorder && !readOnly && (
                <div className="transcribe-container">
                    <AudioRecorder
                        className="transcribe"
                        transcribeUrl={(audioRecorder as AudioRecorder)?.url}
                        onTranscribe={(value) => (onTranscribe ? onTranscribe(value) : handleTranscribe(value))}
                        onStop={(audioRecorder as AudioRecorder)?.onStop}
                        onMouseDown={handleMouseDown}
                    />
                </div>
            )}
        </div>
    );
};

export const isRichTextInputEmpty = (value: string): boolean => {
    const regex = /(<([^>]+)>)/gi;
    return !value.replace(regex, "").length;
};
